import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import {
  Appearance,
  AppearanceType,
  Background,
  BackgroundType,
  Size
} from '@Components/designConstants';
import { useSetAreYouSure } from '../../redux/modals';

export const CheckPlacement = {
  LEADING: 'leading',
  TRAILING: 'trailing'
} as const;
export type CheckPlacementType = (typeof CheckPlacement)[keyof typeof CheckPlacement];

// Opens the "Show run to clients?" / "Hide run from clients?" confirm, then calls onConfirm.
// Shared by the toggle button below and MmmRunSelector's per-run actions menu.
export const useConfirmClientFacingToggle = () => {
  const setAreYouSure = useSetAreYouSure();
  return async (isVisible: boolean, onConfirm: () => void) => {
    try {
      await setAreYouSure({
        title: isVisible ? 'Hide run from clients?' : 'Show run to clients?',
        message: `You're about to make this run ${
          isVisible ? 'hidden from' : 'visible to'
        } clients. Are you sure you want to continue?`,
        cancelText: 'Cancel',
        okayText: 'Continue',
        onOkay: onConfirm
      });
    } catch {
      // Cancel rejects the confirm promise; there's nothing to undo.
    }
  };
};

interface MmmClientFacingToggleProps {
  isVisible: boolean; // current client-facing state (drives the checkbox + wording)
  onConfirm: () => void; // called on the AreYouSure "Continue" — each page passes its own toggle fn
  className?: string;
  background?: BackgroundType;
  appearance?: AppearanceType;
  checkPlacement?: CheckPlacementType;
  'data-testid'?: string;
}

export const MmmClientFacingToggle = ({
  isVisible,
  onConfirm,
  className,
  background = Background.LIGHT,
  appearance = Appearance.FILLED,
  checkPlacement = CheckPlacement.TRAILING,
  'data-testid': dataTestId
}: MmmClientFacingToggleProps) => {
  const confirmClientFacingToggle = useConfirmClientFacingToggle();
  const check = <Checkbox checked={isVisible} readOnly />;

  return (
    <Button
      size={Size.LG}
      className={className}
      background={background}
      appearance={appearance}
      data-testid={dataTestId}
      leadingElement={checkPlacement === CheckPlacement.LEADING ? check : undefined}
      trailingElement={checkPlacement === CheckPlacement.TRAILING ? check : undefined}
      onClick={() => confirmClientFacingToggle(isVisible, onConfirm)}
    >
      Client-Facing
    </Button>
  );
};
